import { ApiResponse } from '../../../../../types/api-response.types';
import type { WORKFLOW_CONDITION_SKIP_RE_LOGIN } from '@const/workflow-condition-keys.const';

export enum StepTypeEnum {
  INPUT = 'input',
  CLICK = 'click',
  SELECT = 'select',
  WAIT = 'wait',
  CLOSE = 'close',
  PRESS = 'press',
  CAPTCHA = 'captcha',
  NAVIGATE = 'navigate',
  PAUSE = 'pause',
  ATTACH = 'attach',
  TWO_FA = '2fa',
  EXTRACT = 'extract',
  EXTRACT_TABLE = 'extractTable',
}

export enum FindByEnum {
  CSS = 'css',
  XPATH = 'xpath',
  ID = 'id',
  NAME = 'name',
  CLASS = 'class',
  TEXT = 'text',
  PLACEHOLDER = 'placeholder',
  TYPE = 'type',
}

export enum DataSourceEnum {
  APPLICATION = 'application',
}

export interface SkipReLoginCondition {
  login_check_step: string;
  jump_to_step: string;
}

export type WorkflowCondition = {
  [WORKFLOW_CONDITION_SKIP_RE_LOGIN]?: SkipReLoginCondition;
  // More condition keys can be added here with their specific types
};

export interface WaitForSelector {
  state?: 'visible' | 'hidden' | 'attached' | 'detached';
  timeoutMs?: number;
}

export interface IframeContext {
  autoDetect?: boolean;
  selector?: string;
}

export interface StepFind {
  by: FindByEnum | '';
  value: string | '';
}

export interface StepConfig {
  type: StepTypeEnum;
  find: StepFind;
  value?: string | (() => string);
  waitForPageLoad?: 'load' | 'domcontentloaded' | 'networkidle';
  waitForSelector?: boolean | WaitForSelector;
  iframeContext?: IframeContext;
  extractAttribute?: string; // For extract step: 'text', 'value', 'innerText', 'selectedValue', 'selectedText', or any attribute name (e.g., 'href', 'src', 'data-id')
  extractColumns?: string[]; // For extractTable step: specify which column headers to extract (e.g., ["Passport Number", "Status"])
}

export interface WorkflowStep {
  order: number;
  stepName?: string;
  step: StepConfig;
  delayMs?: number;
}

export interface WorkflowData {
  name: string;
  steps: WorkflowStep[];
  condition?: WorkflowCondition;
}

export interface WorkflowFormData extends WorkflowData {
  mappings?: WorkflowMappings;
}

export interface WorkflowMappings {
  dataSource: DataSourceEnum;
  fieldsMap: Record<string, string>;
}

// Workflow entity (what comes from API)
export interface Workflow {
  id: string;
  tenantId: string;
  name: string;
  steps: WorkflowStep[];
  mappings?: WorkflowMappings;
  condition?: WorkflowCondition;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;
}

// API types
export interface WorkflowCreate {
  tenantId: string;
  name: string;
  steps: WorkflowStep[];
  mappings?: WorkflowMappings;
  condition?: WorkflowCondition;
}

export interface WorkflowUpdate {
  name?: string;
  steps?: WorkflowStep[];
  mappings?: WorkflowMappings;
  condition?: WorkflowCondition;
}

export interface WorkflowGetResponse extends ApiResponse<Workflow> {
  data: Workflow;
}

export interface WorkflowGetFiltersResponse extends ApiResponse<any> {
  data: any;
}
